import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import {
  apiDeleteMonitoringSettingsQuestionnairesUuid,
  apiPutMonitoringSettingsQuestionnairesUuid,
} from "../../../api";
import { AppModel } from "../../../app/app-model";
import { useCountries } from "../../../catalogs/countries";
import { AddButton } from "../../../components/ui-kit/add-button";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import {
  monitoringTypes,
  questionnaireTypes,
} from "../../../constants/enum-to-text";
import {
  getCountryOptions,
  getRegionOptions,
  regionDisabled,
  regionPredicate,
} from "../../../utils/filter-presets";
import { assert } from "../../../utils/utils";
import { AddQuestionnaireSettingsDialog } from "./add-questionnaire-settings-dialog";
import { formatDuration } from "./duration-utils";
import { EditQuestionnaireSettingsDialog } from "./edit-questionnaire-settings-dialog";
import { stringifyFrequency } from "./questionnaire-settings";

export const QuestionnaireSettingsRoute = observer(
  function QuestionnaireSettingsRoute(props: { app: AppModel }) {
    const { t } = useLingui();
    const admin = props.app.requireAdminShit();
    const model = admin.questionnaireSettings;
    assert(model, "Questionnaire settings model not initialized");
    const countries = useCountries();

    const [showAddDialog, setShowAddDialog] = useState(false);

    return (
      <>
        {showAddDialog && (
          <AddQuestionnaireSettingsDialog
            admin={admin}
            onClose={() => setShowAddDialog(false)}
            onSaved={() => {
              void admin.refreshQuestionnaireSettings();
            }}
          />
        )}

        <DataTable
          persistState
          editMode="dialog"
          itemKeyField="uuid"
          filterNoun={(count) =>
            t`${plural(count, { one: "setting", other: "settings" })}`
          }
          filterActions={
            <AddButton onClick={() => setShowAddDialog(true)}>
              <Trans>New questionnaire settings</Trans>
            </AddButton>
          }
          columns={[
            {
              header: <Trans>Questionnaire</Trans>,
              attr: "questionnaire_id",
              type: "enum",
              values: questionnaireTypes,
              filter: {
                kind: "multi",
                group: t`Settings`,
                options: new Map(Object.entries(questionnaireTypes)),
              },
              cell: ({ item }) => item.questionnaireText,
            },
            {
              header: <Trans>Monitoring type</Trans>,
              attr: "monitoring_type",
              type: "enum",
              values: monitoringTypes,
              filter: {
                kind: "multi",
                group: t`Settings`,
                options: new Map(Object.entries(monitoringTypes)),
              },
              cell: ({ item }) => item.monitoringTypeText,
            },
            {
              header: <Trans>Country</Trans>,
              attr: "country",
              type: "enum",
              values: countries.countriesEnum,
              filter: {
                kind: "multi",
                group: t`Location`,
                options: getCountryOptions,
              },
              cell: ({ item }) => item.country,
            },
            {
              header: <Trans>Region</Trans>,
              attr: "region",
              filter: {
                kind: "multi",
                group: t`Location`,
                options: (table) => getRegionOptions(table, countries),
                disabled: regionDisabled,
                disabledMessage: t`Select a country first`,
                predicate: regionPredicate,
              },
              cell: ({ item }) => {
                const regions =
                  item.country != null
                    ? countries.getRegionsEnum(item.country)
                    : undefined;
                return regions?.[item.region as any] ?? item.region;
              },
            },
            {
              header: <Trans>First when active</Trans>,
              attr: "first_when_active",
              type: "boolean",
              width: 100,
            },
            {
              header: <Trans>Skippable</Trans>,
              attr: "skippable",
              type: "boolean",
              width: 100,
            },
            {
              header: <Trans>Filling frequency</Trans>,
              attr: "filling_frequency",
              cell: ({ item }) => stringifyFrequency(item.filling_frequency),
            },
            {
              header: <Trans>First after active</Trans>,
              attr: "first_after_active",
              cell: ({ item }) => formatDuration(item.first_after_active),
            },
            {
              header: <Trans>Stop after N</Trans>,
              attr: "stop_after_n",
              type: "number",
            },
            {
              header: <Trans>Stop after time</Trans>,
              attr: "stop_after_time_from_first",
              cell: ({ item }) =>
                formatDuration(item.stop_after_time_from_first),
            },
            {
              header: <Trans>Clinic</Trans>,
              cell: ({ item }) => item.clinicName,
            },
            {
              header: <Trans>Physician</Trans>,
              cell: ({ item }) => item.physicianName,
            },
            {
              header: <Trans>Created</Trans>,
              attr: "created_at",
              type: "datetime",
              sort: true,
            },
            {
              header: <Trans>Updated</Trans>,
              attr: "updated_at",
              type: "datetime",
              sort: true,
            },
          ]}
          model={model}
          renderEditDialog={({ item, onClose, onSave }) => (
            <EditQuestionnaireSettingsDialog
              item={item}
              admin={admin}
              onClose={onClose}
              onSave={onSave}
            />
          )}
          onEdit={async (patch, item) => {
            await apiPutMonitoringSettingsQuestionnairesUuid({
              path: { uuid: item.uuid },
              body: patch,
            });
            window.location.reload();
          }}
          onDelete={async (item) => {
            await apiDeleteMonitoringSettingsQuestionnairesUuid({
              path: { uuid: item.uuid },
            });
            window.location.reload();
          }}
          rowsPerPage={10}
        />
      </>
    );
  },
);
